import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, graphRequest } from '../authConfig';
import { api } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Signing in with Microsoft is only half of getting in — the API still has to
  // recognise the account. Someone with no INV group membership authenticates
  // fine and then gets a 403 from /auth/me carrying a message written for them
  // ("Contact IT to request access"). Holding that here is what lets the app
  // show it, instead of the spinner that used to hang forever in its place.
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // `acquireIdToken` and `withAuth` are permanently stable, and that matters
  // more than it looks. Every list page builds its fetch as
  // `useCallback(..., [withAuth, ...])` and runs it from
  // `useEffect(() => load(), [load])`, so a `withAuth` that changed identity on
  // each render would make `load` change too, refire the effect, re-render, and
  // fetch again — forever. It is not a hypothetical: swapping in a context whose
  // `withAuth` was rebuilt per render sent the Integrations page into exactly
  // that loop, firing its four history requests without end.
  //
  // The old dependency list was [instance, accounts], which held only because
  // MSAL happens to hand back a stable `accounts` array. Nothing in the app
  // guaranteed that, and the failure mode if a future version stopped is a
  // silent request storm against the API rather than an error anyone would see.
  //
  // Reading both through a ref that is refreshed on every render keeps the
  // callbacks stable while still always using the current values.
  const msalRef = useRef({ instance, accounts });
  msalRef.current = { instance, accounts };

  const acquireIdToken = useCallback(async () => {
    const { instance: msal, accounts: accts } = msalRef.current;
    if (!accts[0]) return null;
    try {
      // Try silent first
      const result = await msal.acquireTokenSilent({ ...loginRequest, account: accts[0] });
      return result.idToken;
    } catch (e) {
      try {
        // Force refresh if silent fails
        const result = await msal.acquireTokenSilent({ ...loginRequest, account: accts[0], forceRefresh: true });
        return result.idToken;
      } catch (e2) {
        try {
          // Fall back to popup if refresh fails
          const result = await msal.acquireTokenPopup(loginRequest);
          return result.idToken;
        } catch (err) { return null; }
      }
    }
  }, []);

  // Reloading the profile when the signed-in account changes used to happen as a
  // side effect of `acquireIdToken`'s identity shifting with `accounts`. Now
  // that it is stable, the effect below has to say so outright — otherwise
  // switching account would keep the previous profile, since `isAuthenticated`
  // never flips.
  const activeAccountId = accounts[0]?.homeAccountId;

  useEffect(() => {
    let cancelled = false;

    async function loadUser() {
      // MSAL is mid-popup or mid-redirect. Return without touching `loading`:
      // the effect re-runs when inProgress settles, and leaving it true keeps
      // the app on the spinner rather than briefly rendering a verdict.
      if (inProgress !== InteractionStatus.None) return;
      if (!isAuthenticated) { setLoading(false); return; }

      // isAuthenticated can flip true after an earlier pass already cleared
      // loading. Without this, the render between here and the first await
      // would see "finished, no user, no error" and show the failure screen.
      setLoading(true);
      setError(null);

      try {
        const idToken = await acquireIdToken();
        if (!idToken) {
          throw new Error('Microsoft did not return a sign-in token. Try signing in again.');
        }
        api.setToken(idToken);

        let me;
        try {
          me = await api.getMe();
        } catch (err) {
          // Only a 401 is worth re-authenticating for. A 403 means the token is
          // valid and the account simply has no access — logging in again asks
          // the same question and gets the same answer, so let it surface.
          if (err.status !== 401) throw err;
          const result = await instance
            .loginPopup({ ...loginRequest, extraScopesToConsent: graphRequest.scopes, prompt: 'none' })
            .catch(() => instance.loginPopup({ ...loginRequest, extraScopesToConsent: graphRequest.scopes }));
          if (!result?.idToken) throw new Error('Sign-in did not return a token.');
          api.setToken(result.idToken);
          me = await api.getMe();
        }

        if (!cancelled) setUser(me);
      } catch (err) {
        console.error('Failed to load user:', err);
        // Every path out of here now ends in a state the UI can explain. The
        // old version logged and fell through, leaving user null and loading
        // false — which RequireAuth read as "still loading", forever.
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadUser();
    return () => { cancelled = true; };
  }, [isAuthenticated, inProgress, acquireIdToken, instance, activeAccountId, reloadKey]);

  // Lets the failure screen re-run the whole load without a page reload.
  const retry = useCallback(() => setReloadKey(k => k + 1), []);

  const withAuth = useCallback(async (fn) => {
    const idToken = await acquireIdToken();
    if (idToken) api.setToken(idToken);
    return fn();
  }, [acquireIdToken]);

  const login = async () => {
    try {
      await instance.loginPopup({ ...loginRequest, extraScopesToConsent: graphRequest.scopes });
    } catch (err) { console.error('Login failed', err); }
  };

  const logout = () => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin + '/login' });

  // Proactively refresh token every 45 minutes to prevent expiry
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(async () => {
      try {
        const freshToken = await acquireIdToken();
        if (freshToken) api.setToken(freshToken);
      } catch (e) {
        console.warn('Proactive token refresh failed:', e);
      }
    }, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, acquireIdToken]);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, withAuth, retry, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
